// Procurando mesa na Liga da Semana (canvas B02): a busca começa na divisão do jogador e abre uma divisão para
// cima e para baixo a cada 20 s; com 60 s, bots completam a mesa. Pareado, vai para a mesa (com patrocinador,
// a tela de entrada aparece lá, antes das cartas). Grátis sem partidas hoje vai para a tela do limite.
// A fila aparece só em números: quantos procuram mesa e quantos estão na sua faixa, que preenchem os lugares
// como "Jogador encontrado", sem nome nem rosto até a mesa fechar. Com trio fixo, os parceiros já sentam em NÓS.
import { useEffect, useRef, useState } from 'react';
import { BOTS_DEPOIS_MS, NOME_DA_LIGA_PADRAO, nomeDaFaixa } from '@truco6/gamificacao';
import type { LigaDaSala, TrioReunindo } from '@truco6/mesa/protocolo';
import { type PainelLiga, lerLiga } from '../comum/api.ts';
import { config } from '../comum/config.ts';
import type { PerfilConta, Sessao } from '../comum/conta.ts';
import { medir } from '../comum/medicao.ts';
import { ir } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { FolhaCelular } from '../comum/FolhaCelular.tsx';
import { BotaoAmizade } from '../comum/BotaoAmizade.tsx';
import { useAgora } from '../comum/useMesa.ts';
import { Tela } from '../telas/comum.tsx';
import { ConexaoBusca, type EstadoBusca } from './conexao.ts';
import { FaixaLiga, NOTA_SEM_RANKING, tempoAte } from './comum.tsx';
import { falaDosLugares, lugaresDaBusca, textoDaFila } from './lugares.ts';

// Quanto tempo o placar da mesa em andamento fica na tela antes de ir para ela.
const MOSTRAR_ANDAMENTO_MS = 2_500;
const mmss = (ms: number) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };

const DICAS = [
  'Na mão de onze, seu trio vê as cartas dos parceiros e decide se joga ou corre.',
  'Empachou a primeira? Quem ganhar a segunda leva a mão.',
  'Da segunda rodada em diante, todo mundo pode esconder a carta. Depois de uma rodada empachada, a seguinte é toda aberta.',
  'O Zap é o 4 de paus quando a vira é 3. A manilha é sempre a carta seguinte à vira.',
  'Truco recebido se decide por maioria do trio, com 15 s na tela.',
];

// Lugar relativo na mesa da busca: 0 embaixo (você) e, no sentido da mesa, esquerda, topo e direita.
const POS: [number, number][] = [[160, 268], [42, 204], [42, 92], [160, 0], [278, 92], [278, 204]];

const LIGA_PADRAO: LigaDaSala = { id: 'padrao', nome: NOME_DA_LIGA_PADRAO, marca: null, premio: null, inicio: 0, fim: 0 };

const SAIDA_MS = 350;

// Quantos lugares a tela mostra: sobe na hora; quando desce, o último lugar faz a animação ao contrário antes.
function useMostrados(alvo: number): [mostrados: number, alvo: number] {
  const [mostrados, setMostrados] = useState(alvo);
  useEffect(() => {
    if (alvo >= mostrados) { setMostrados(alvo); return; }
    const t = setTimeout(() => setMostrados(alvo), SAIDA_MS);
    return () => clearTimeout(t);
  }, [alvo]);
  return [Math.max(mostrados, alvo), alvo];
}

export function BuscaLiga({ sessao, perfil, trio = null }: { sessao: Sessao; perfil: PerfilConta; trio?: string | null }) {
  const [e, setE] = useState<EstadoBusca | null>(null);
  const [painel, setPainel] = useState<PainelLiga | null>(null);
  const agora = useAgora(250);
  const conexao = useRef<ConexaoBusca | null>(null);
  // Conta Google sem celular: a mesa recusa a busca e a folha abre. Salvou, a busca começa de novo.
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    void lerLiga(sessao).then((p) => { if (vivo) setPainel(p); }, () => {});
    medir('busca_iniciada');
    const c = conexao.current = new ConexaoBusca(config.mesaUrl, () => sessao.token(), { apelido: perfil.apelido, avatar: perfil.avatar }, setE, trio).iniciar();
    return () => { vivo = false; c.cancelar(); };
  }, [sessao.id, tentativa]);

  // Pareado: a mesa. Na vaga de uma mesa em andamento, o placar e "Você entra na próxima mão" antes de ir para ela.
  // Sem partidas hoje: a tela do limite.
  useEffect(() => {
    if (e?.fase !== 'pareado' || !e.codigo) return;
    const codigo = e.codigo;
    if (!e.andamento) { ir({ tela: 'sala', codigo }, true); return; }
    const t = setTimeout(() => ir({ tela: 'sala', codigo }, true), MOSTRAR_ANDAMENTO_MS);
    return () => clearTimeout(t);
  }, [e?.fase]);
  useEffect(() => { if (e?.erro?.codigo === 'sem_partidas') ir({ tela: 'limite' }, true); }, [e?.erro?.codigo]);

  const cancelar = () => { conexao.current?.cancelar(); ir({ tela: 'inicio' }, true); };
  const liga = painel?.liga ?? e?.liga ?? LIGA_PADRAO;
  const lim = painel?.limite;
  const chip = lim && lim.limite !== null
    ? (lim.usadas < lim.limite ? `Partida ${lim.usadas + 1} de ${lim.limite}` : 'Partida bônus')
    : liga.fim ? tempoAte(liga.fim, agora) : null;
  const espera = e?.desde ? Math.max(0, agora - e.desde) : 0;
  const bots = BOTS_DEPOIS_MS - espera;
  const dica = DICAS[Math.floor(espera / 8_000) % DICAS.length]!;
  const faltaCelular = e?.fase === 'erro' && e.erro?.codigo === 'falta_celular';
  const falhou = e?.fase === 'erro' && e.erro?.codigo !== 'sem_partidas' && !faltaCelular;
  const parceiros = e?.trio?.parceiros ?? [];
  const [mostrados, alvo] = useMostrados(Math.min(5, e?.naFaixa ?? 0));
  const lugares = lugaresDaBusca(mostrados, parceiros), destino = lugaresDaBusca(alvo, parceiros);

  return (
    <Tela larga>
      <div className="busca">
      <FaixaLiga liga={liga} chip={chip} />
      <div className="busca-cab">
        <h1>{e?.fase === 'reunindo' ? 'REUNINDO O TRIO' : 'PROCURANDO MESA'}</h1>
        {e?.fase === 'procurando' && <p className="fila-total"><b>{e.total === null ? '\u00a0' : textoDaFila(e.total)}</b></p>}
        <p className="sub">
          {e?.fase === 'pareado' ? (e.andamento ? 'Vaga encontrada!' : 'Mesa pronta!') : e?.reuniao ? `${e.reuniao.nome}: a busca começa com os 3` : e?.trio ? `Com o trio ${e.trio.nome}` : 'Adversários do seu nível'}
        </p>
      </div>
      {e?.fase === 'reunindo' && e.reuniao && <Reuniao r={e.reuniao} eu={sessao.id} />}
      {e?.fase === 'pareado' && e.andamento && (
        <p className="cartao andamento-liga" role="status">
          <b>Mesa em andamento: {e.andamento.nos} x {e.andamento.eles}.</b> Você entra na próxima mão.
        </p>
      )}
      {e?.fase !== 'reunindo' && <section className="busca-mesa" aria-label="Lugares da mesa">
        <div className="mini-mesa">
          <div className="hex mini-madeira" aria-hidden="true" />
          <div className="hex felt mini-feltro" aria-hidden="true" />
          {lugares.map((l) => {
            const pos = POS[l.r]!, cor = l.nos ? 'var(--nos-400)' : 'var(--eles-400)';
            const saindo = l.tipo === 'encontrado' && destino[l.r]!.tipo === 'procurando';
            return (
              <div key={l.r} className={'lugar' + (l.tipo === 'voce' ? ' meu' : '')} style={{ left: pos[0], top: pos[1] }}>
                {l.tipo === 'voce' ? <Rosto perfil={perfil} usuario={sessao.id} cor={cor} tamanho={50} />
                  : l.tipo === 'parceiro' ? <Rosto perfil={l.parceiro} cor={cor} tamanho={50} />
                  : l.tipo === 'encontrado' ? <span key="encontrado" className={'mais encontrado' + (saindo ? ' saindo' : '')} style={{ borderColor: cor }} aria-hidden="true"><Silhueta /></span>
                  : <span key="procurando" className="mais procurando" style={{ borderColor: cor }} aria-hidden="true" />}
                <span className={'nm' + (l.tipo === 'encontrado' ? ' encontrado' : '')}>
                  {l.tipo === 'voce' ? 'Você' : l.tipo === 'parceiro' ? l.parceiro!.apelido : l.tipo === 'encontrado' ? 'Jogador encontrado' : 'Procurando'}
                </span>
                <span className="trio-tag" style={{ color: cor }}>{l.nos ? 'NÓS' : 'ELES'}</span>
              </div>
            );
          })}
        </div>
        <p className="so-leitor" aria-live="polite">{e?.fase === 'procurando' ? falaDosLugares(destino) : ''}</p>
      </section>}
      {falhou ? (
        <div className="busca-tempo">
          <p className="erro centro" role="alert">{e?.erro?.mensagem}</p>
          <button className="btn1 largo" onClick={() => location.reload()}>TENTAR DE NOVO</button>
        </div>
      ) : e?.fase === 'reunindo' ? null : (
        <div className="busca-tempo" role="status">
          <p>
            {e?.fase === 'procurando' ? <>Na divisão <b>{nomeDaFaixa(e.divisao, espera)}</b></> : e?.fase === 'pareado' ? 'Indo para a mesa' : 'Conectando'}
            {' · '}<span className="relogio-busca">{mmss(espera)}</span>
          </p>
          {e?.fase !== 'pareado' && (
            <p className="nota">
              {bots > 0 ? `Se faltar gente, bots completam a mesa em ${mmss(bots)}.` : 'Completando a mesa com bots.'}
              {e?.fase === 'procurando' && destino.filter((l) => l.tipo === 'procurando').length >= 3 && <> {NOTA_SEM_RANKING}</>}
            </p>
          )}
        </div>
      )}
      <section className="cartao dica-truco" aria-label="Dica de truco">
        <span className="dica-cab"><IconeLampada />DICA DE TRUCO</span>
        <p>{dica}</p>
      </section>
      <button className="link" onClick={cancelar}>Cancelar</button>
      </div>
      {faltaCelular && <FolhaCelular sessao={sessao} aoSalvar={() => { setE(null); setTentativa((n) => n + 1); }} aoFechar={cancelar} />}
    </Tela>
  );
}

// Quem do trio já pediu a busca. Falta alguém: o convite vai pelo WhatsApp com o link da busca do trio.
function Reuniao({ r, eu }: { r: TrioReunindo; eu: string }) {
  const link = `${location.origin}/liga?trio=${r.id}`;
  return (
    <section className="cartao reuniao" aria-label={`Trio ${r.nome}`}>
      <ul>
        {r.membros.map((m) => (
          <li key={m.usuario} className={m.pronto ? 'pronto' : undefined}>
            <span aria-hidden="true">{m.pronto ? '✓' : '…'}</span>
            <b>{m.usuario === eu ? 'Você' : m.apelido ?? 'Parceiro do trio'}</b>
            <small>{m.pronto ? 'Pronto' : 'Ainda não entrou na busca'}</small>
            {m.usuario !== eu && m.apelido && <BotaoAmizade usuario={m.usuario} apelido={m.apelido} compacto />}
          </li>
        ))}
      </ul>
      <a className="btn2 whats largo" href={'https://wa.me/?text=' + encodeURIComponent(`Bora jogar a liga com o trio ${r.nome}? Entra aqui: ${link}`)} target="_blank" rel="noreferrer">
        Chamar o trio no WhatsApp
      </a>
    </section>
  );
}

// Rosto de quem foi encontrado: só a silhueta, sem nome nem rosto até a mesa fechar.
function Silhueta() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="8" r="4.2" />
      <path d="M3.5 21c.6-4.4 4.1-7.2 8.5-7.2s7.9 2.8 8.5 7.2z" />
    </svg>
  );
}

function IconeLampada() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2.1h5c0-.9.4-1.6 1-2.1A6 6 0 0 0 12 3z" />
    </svg>
  );
}
